// Presentational sub-components for the S7Comm server page — field/number
// inputs, metrics, quality rejection, semantic diff, and the review/rollback
// modals. Split from plc-s7comm-server.js to keep each module focused.
import React from "react";

import xmarkIcon from "../../../assets/xmark.svg";
import Select from "../../../components/Select";
import TagPicker from "../../../components/TagPicker";
import { GatewayStatusPill } from "../../../components/GatewayStatus";
import { FieldError, NumberField, errorMessage } from "./plc-protocol-server-fields";
import { formatProtocolServerDateTime } from "./plc-protocol-servers-model";
import {
  S7_FIELD_TYPES,
  formatS7SemanticValue,
  normalizeS7SemanticDiff,
} from "./plc-s7comm-server-model";

const formatDateTime = formatProtocolServerDateTime;

const displayNumber = (value) =>
  Number.isFinite(Number(value)) ? Number(value).toLocaleString() : "0";

const semanticOperationLabel = (operation) => ({
  added: "Added",
  removed: "Removed",
  changed: "Changed",
}[operation] || "Changed");

const configAuditActionLabel = (action) => ({
  configure: "Configure",
  enable: "Enable listener",
  disable: "Disable listener",
}[action] || "Configure");

const configAuditOutcomeLabel = (outcome) => ({
  proposed: "Proposal received",
  previewed: "Preview ready",
  confirmed: "Confirmed · applying",
  desired_only: "Saved · configuration only",
  applied: "Applied",
  rejected: "Rejected",
  apply_failed: "Apply failed",
  expired: "Preview expired",
}[outcome] || "Rejected");

const integrityLabel = (revision, _profile, sealed) => {
  if (!revision) return "—";
  return sealed ? "Verified" : "Cannot be restored";
};

const Metric = ({ label, value }) => (
  <div className="plcProtocolMetric">
    <span>{label}</span>
    <strong>{displayNumber(value)}</strong>
  </div>
);

const QualityRejection = ({ entry }) => {
  const reasons = [
    ["Missing", entry.missing],
    ["Non-Good", entry.nonGood],
    ["Stale", entry.stale],
    ["Conversion", entry.conversion],
    ["Snapshot unavailable", entry.snapshotUnavailable],
  ].filter(([, count]) => count > 0);
  return (
    <div className="plcS7QualityRow">
      <div>
        <strong>{entry.tagPath}</strong>
        <span>{reasons.map(([label, count]) => `${label}: ${displayNumber(count)}`).join(" · ") || "No current rejection count"}</span>
      </div>
      <small>Last rejected: {formatDateTime(entry.lastRejectedAt)}</small>
    </div>
  );
};

const S7StatusPill = ({ status }) => {
  let label = "Configuration only";
  let tone = "unavailable";
  if (status?.executable) {
    if ((status.desiredRevision || status.appliedRevision) && !status.reconciled) {
      label = "Needs reconciliation";
      tone = "error";
    } else if (status.lifecycle === "degraded" || status.runtime.lifecycle === "degraded") {
      label = status.runtime.listening ? "Listening · degraded" : "Degraded";
      tone = "starting";
    } else if (status.runtime.listening) {
      label = "Listening";
      tone = "running";
    } else if (status.lifecycle === "faulted" || status.lastError) {
      label = "Needs attention";
      tone = "error";
    } else {
      label = status.runtime.enabled ? "Starting" : "Disabled";
      tone = status.runtime.enabled ? "starting" : "disabled";
    }
  }
  return (
    <GatewayStatusPill className={`plcProtocolStatus is-${tone}`} indicator>{label}</GatewayStatusPill>
  );
};

const SemanticDiff = ({ changes }) => (
  <div className="plcS7SemanticDiff">
    <div className="plcS7DiffHeader">
      <strong>Changes</strong>
      <span>{changes.length} change{changes.length === 1 ? "" : "s"}</span>
    </div>
    {changes.length ? changes.map((change, index) => (
      <div className="plcS7DiffRow" key={`${change.path}-${index}`}>
        <div className="plcS7DiffIdentity">
          <span className={`plcS7DiffOperation is-${change.operation}`}>{semanticOperationLabel(change.operation)}</span>
          <code>{change.path}</code>
        </div>
        <div className="plcS7DiffValues">
          <div><span>Before</span><pre>{formatS7SemanticValue(change.before)}</pre></div>
          <div><span>After</span><pre>{formatS7SemanticValue(change.after)}</pre></div>
        </div>
      </div>
    )) : <div className="gwEmptyState gwEmptyCompact"><div className="gwEmptyBody">The server reports no semantic change.</div></div>}
  </div>
);

const ReviewModal = ({ preview, saving, acknowledged, onAcknowledge, onCancel, onConfirm }) => {
  if (!preview) return null;
  const config = preview.config || {};
  const semanticDiff = normalizeS7SemanticDiff(preview.semantic_diff);
  const fieldCount = (config.data_blocks || []).reduce(
    (sum, db) => sum + (Array.isArray(db.fields) ? db.fields.length : 0),
    0
  );
  const acknowledgementRequired = preview.confirmation_required === true;
  return (
    <div className="gwOverlay" onClick={() => !saving && onCancel()} style={{ zIndex: 115 }}>
      <div className="gwModal plcS7ReviewModal" onClick={(event) => event.stopPropagation()}>
        <div className="gwModalHeader">
          <div className="gwModalTitle">Review S7Comm settings</div>
          <button className="gwModalClose" type="button" onClick={onCancel} disabled={saving}>
            <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
          </button>
        </div>
        <div className="gwModalBody plcS7ReviewBody">
          <div className="plcProtocolRuntimeSummary">
            <div><span>Action</span><strong>{config.enabled ? "Start or update server" : "Save with server off"}</strong></div>
            <div><span>Endpoint</span><strong>{config.bind_address}:{config.port}</strong></div>
            <div><span>Data blocks</span><strong>{(config.data_blocks || []).length}</strong></div>
            <div><span>Mapped fields</span><strong>{fieldCount}</strong></div>
          </div>
          <div className="plcProtocolExposureWarning" role="note">
            <strong>Classic S7Comm is not authenticated or encrypted</strong>
            <span>IP addresses and TSAP values are attribution only. Keep this endpoint on a trusted, segmented plant network; never expose it to the Internet or use it as a safety/security boundary.</span>
          </div>
          {(preview.warnings || []).length ? (
            <ul className="plcOpcuaWarningList">
              {preview.warnings.map((warning) => <li key={warning}>{warning}</li>)}
            </ul>
          ) : null}
          <SemanticDiff changes={semanticDiff} />
          {acknowledgementRequired ? (
            <label className="plcS7Acknowledge">
              <input
                type="checkbox"
                checked={acknowledged}
                disabled={saving}
                onChange={(event) => onAcknowledge(event.target.checked)}
              />
              <span>I understand this listener has no client authentication, encryption, message integrity, or replay protection.</span>
            </label>
          ) : null}
        </div>
        <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
          <button className="gwGhostBtn" type="button" onClick={onCancel} disabled={saving}>Cancel</button>
          <button
            className={config.enabled ? "gwDangerBtn" : "gwPrimaryBtn"}
            type="button"
            disabled={saving || (acknowledgementRequired && !acknowledged)}
            onClick={onConfirm}
          >
            {saving ? "Applying…" : config.enabled ? "Confirm and apply" : "Save settings"}
          </button>
        </div>
      </div>
    </div>
  );
};

const RollbackReviewModal = ({ preview, saving, acknowledged, hasUnsavedChanges, onAcknowledge, onCancel, onConfirm }) => {
  if (!preview) return null;
  const config = preview.config || {};
  return (
    <div className="gwOverlay" onClick={() => !saving && onCancel()} style={{ zIndex: 116 }}>
      <div className="gwModal plcS7RollbackModal" onClick={(event) => event.stopPropagation()}>
        <div className="gwModalHeader">
          <div className="gwModalTitle">Review saved S7Comm settings</div>
          <button className="gwModalClose" type="button" onClick={onCancel} disabled={saving}>
            <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
          </button>
        </div>
        <div className="gwModalBody plcS7ReviewBody">
          <div className="plcProtocolRuntimeSummary">
            <div><span>Server</span><strong>{config.enabled ? "On" : "Off"}</strong></div>
            <div><span>Endpoint</span><strong>{config.bind_address || "—"}:{config.port || "—"}</strong></div>
          </div>
          <div className="plcProtocolExposureWarning" role="note">
            <strong>Your current saved settings stay recoverable</strong>
            <span>Control Seat verifies that this review is still current before restoring it. If another change was saved, reopen the review and try again.</span>
          </div>
          {hasUnsavedChanges ? (
            <div className="plcProtocolRuntimeError" role="alert">
              <strong>Unsaved editor changes will be discarded</strong>
              <span>This restores the saved settings you selected, not the unsaved fields currently visible behind this review.</span>
            </div>
          ) : null}
          <SemanticDiff changes={preview.semanticDiff} />
          {(preview.warnings || []).length ? (
            <ul className="plcOpcuaWarningList">
              {preview.warnings.map((warning) => <li key={warning}>{warning}</li>)}
            </ul>
          ) : null}
          {preview.noAuthenticationAcknowledgementRequired ? (
            <label className="plcS7Acknowledge">
              <input
                type="checkbox"
                checked={acknowledged}
                disabled={saving}
                onChange={(event) => onAcknowledge(event.target.checked)}
              />
              <span>I understand the restored settings expose classic S7Comm without client authentication, encryption, message integrity, or replay protection.</span>
            </label>
          ) : null}
        </div>
        <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
          <button className="gwGhostBtn" type="button" onClick={onCancel} disabled={saving}>Cancel</button>
          <button
            className={config.enabled ? "gwDangerBtn" : "gwPrimaryBtn"}
            type="button"
            disabled={saving || (preview.noAuthenticationAcknowledgementRequired && !acknowledged)}
            onClick={onConfirm}
          >
            {saving ? "Restoring…" : "Restore settings"}
          </button>
        </div>
      </div>
    </div>
  );
};

const S7FieldEditor = ({ field, dbIndex, fieldIndex, error, disabled, onChange, onRemove }) => {
  const selectedType = S7_FIELD_TYPES.find((candidate) => candidate.value === field.type);
  const set = (property) => (event) => onChange(dbIndex, fieldIndex, {
    [property]: property === "writable" ? event : event.target.value,
  });
  return (
    <div className="plcS7Field" data-gw-resource="s7comm_server_mapping">
      <div className="plcProtocolMappingHeader">
        <div>
          <strong>Field {fieldIndex + 1}</strong>
          <span className={`plcProtocolAccess ${field.writable ? "isWritable" : ""}`}>
            {field.writable ? "Read / write" : "Read only"}
          </span>
        </div>
        <button className="gwIconButton" type="button" disabled={disabled} onClick={() => onRemove(dbIndex, fieldIndex)} aria-label={`Remove field ${fieldIndex + 1}`} title="Remove field">
          <img src={xmarkIcon} alt="" className="gwIcon" />
        </button>
      </div>
      <div className="plcS7FieldGrid">
        <NumberField
          label="Byte offset"
          value={field.byteOffset}
          min={0}
          max={4095}
          step={1}
          onChange={set("byteOffset")}
          disabled={disabled}
        />
        <NumberField
          label="Bit offset"
          value={field.bitOffset}
          min={0}
          max={7}
          step={1}
          onChange={set("bitOffset")}
          disabled={disabled || field.type !== "BOOL"}
          hint={field.type === "BOOL" ? "Bit 0–7 inside the byte." : "Only BOOL uses a bit offset."}
        />
        <div className="gwField">
          <label className="gwLabel">S7 scalar type</label>
          <Select
            value={field.type}
            options={S7_FIELD_TYPES}
            onChange={(value) => onChange(dbIndex, fieldIndex, { type: value })}
            disabled={disabled}
          />
          <div className="gwHint">Exact big-endian S7 scalar; no implicit scaling or byte swapping.</div>
        </div>
        <div className="gwField plcProtocolTagField">
          <label className="gwLabel">Control Seat tag path</label>
          <TagPicker
            value=""
            displayValue={field.tagPath}
            onChange={(_tagID, path) => onChange(dbIndex, fieldIndex, { tagPath: path })}
            disabled={disabled}
            placeholder="Search or choose a tag…"
          />
          <div className="gwHint">Normal unified tag full path. Required type: {selectedType?.tagType || "supported scalar"}.</div>
        </div>
        <div className="gwField">
          <label className="gwLabel">Access</label>
          <button
            type="button"
            className={`gwToggle ${field.writable ? "isOn" : ""} ${disabled ? "isDisabled" : ""}`}
            role="switch"
            aria-checked={field.writable}
            aria-label={`Allow S7 writes for field ${fieldIndex + 1}`}
            disabled={disabled}
            onClick={() => set("writable")(!field.writable)}
          ><span /></button>
          <div className="gwHint">Writes use the normal tag ownership/source path; this is never a PLC force.</div>
        </div>
        <div className="gwField">
          <label className="gwLabel">Resolved tag type</label>
          <input className="gwInput" value={field.tagType || "Resolved during preview"} disabled readOnly />
        </div>
      </div>
      <FieldError>{error}</FieldError>
    </div>
  );
};

const S7DataBlockEditor = ({ db, dbIndex, error, fieldErrors, disabled, onChange, onRemove, onAddField, onChangeField, onRemoveField }) => {
  const set = (property) => (event) => onChange(dbIndex, { [property]: event.target.value });
  return (
    <div className="plcProtocolMapping plcS7DataBlock" data-gw-resource="s7comm_server_data_block">
      <div className="plcProtocolMappingHeader">
        <div>
          <strong>Data block {dbIndex + 1}</strong>
          <span className="plcProtocolAccess">{db.fields.length} field{db.fields.length === 1 ? "" : "s"}</span>
        </div>
        <button className="gwIconButton" type="button" disabled={disabled} onClick={() => onRemove(dbIndex)} aria-label={`Remove data block ${dbIndex + 1}`} title="Remove data block">
          <img src={xmarkIcon} alt="" className="gwIcon" />
        </button>
      </div>
      <div className="plcS7DBGrid">
        <NumberField label="DB number" value={db.number} min={1} max={65535} step={1} onChange={set("number")} disabled={disabled} />
        <NumberField label="Size (bytes)" value={db.size} min={1} max={4096} step={1} onChange={set("size")} disabled={disabled} hint="Fixed zero-filled image, 1–4,096 bytes." />
        <div className="gwField plcS7Description">
          <label className="gwLabel">Description</label>
          <input className="gwInput" value={db.description} maxLength={256} onChange={set("description")} disabled={disabled} placeholder="Line 1 process values" />
        </div>
      </div>
      <FieldError>{error}</FieldError>
      <div className="plcS7Fields">
        {db.fields.map((field, fieldIndex) => (
          <S7FieldEditor
            key={field.key}
            field={field}
            dbIndex={dbIndex}
            fieldIndex={fieldIndex}
            error={fieldErrors?.[fieldIndex]}
            disabled={disabled}
            onChange={onChangeField}
            onRemove={onRemoveField}
          />
        ))}
      </div>
      <button className="gwSecondaryBtn plcProtocolAddButton" type="button" disabled={disabled} onClick={() => onAddField(dbIndex)}>Add field</button>
    </div>
  );
};


export {
  FieldError,
  NumberField,
  Metric,
  QualityRejection,
  ReviewModal,
  RollbackReviewModal,
  S7StatusPill,
  S7DataBlockEditor,
  errorMessage,
  formatDateTime,
  displayNumber,
  integrityLabel,
  configAuditActionLabel,
  configAuditOutcomeLabel,
};
